import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchYouTubeShorts, ShortItem } from '@/services/youtubeShortsService';
import ShortsCard from '@/components/youtube/ShortsCard';
import InlineShortPlayer from '@/components/youtube/InlineShortPlayer';
import ShortsModal from '@/components/youtube/ShortsModal';
import HorizontalScroller from '@/components/mobile/HorizontalScroller';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Skeleton } from '@/components/ui/skeleton';
import youtubeShortsLogo from '@/assets/youtube-shorts-logo.svg';

const YouTubeShortsSection: React.FC = () => {
  const [modalShort, setModalShort] = useState<{ id: string; title: string } | null>(null);
  
  const CHANNEL_HANDLE = 'Everhouseco';
  const STORAGE_KEY = `yt_shorts_cache_v1_${CHANNEL_HANDLE}`;
  const [cachedShorts, setCachedShorts] = useState<ShortItem[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as { items?: ShortItem[] };
      return Array.isArray(parsed.items) ? parsed.items : [];
    } catch {
      return [];
    }
  });

  const { data = [], isLoading, error } = useQuery({
    queryKey: ['youtube-shorts', { channel: CHANNEL_HANDLE, maxResults: 12 }],
    queryFn: () => fetchYouTubeShorts(12, { channel: CHANNEL_HANDLE }),
    staleTime: 1000 * 60 * 60, // 1 hour (matches edge function cache)
    gcTime: 1000 * 60 * 120, // 2 hours garbage collection
    retry: false, // Don't retry on failure, show fallback faster
    refetchOnWindowFocus: false, // Avoid unnecessary refetches
  });

  // Persist successful fetches to localStorage
  useEffect(() => {
    if (data && data.length) {
      setCachedShorts(data);
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ items: data, updatedAt: Date.now() })
        );
      } catch {
        // ignore write errors
      }
    }
  }, [data]);

  // Prefer fresh data; fall back to cached Shorts so the section always shows something
  const displayShorts = data.length ? data : cachedShorts;
  const showError = !isLoading && displayShorts.length === 0;

  return (
    <section className="article-container py-10" aria-labelledby="youtube-shorts-heading">
      <div className="mb-4 flex items-baseline justify-between">
        <div className="flex items-center gap-3">
          <img 
            src="/lovable-uploads/abcfc02e-b77d-432d-aa5b-55b9308be8a8.png" 
            alt="YouTube Shorts" 
            className="h-8 w-auto"
          />
          <h2 className="text-2xl text-foreground">Shorts</h2>
        </div>
        <a
          href="https://www.youtube.com/@Everhouseco/shorts"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm underline hover:no-underline"
          aria-label="View all Everhouse YouTube Shorts on YouTube"
        >
          View on YouTube
        </a>
      </div>

      {isLoading ? (
        <div className="bleed-right-mobile">
          <HorizontalScroller spacing="custom-10px" disableScale={true} snap={true} snapAlign="start" snapStopAlways={true} showNextCard={true} noLeftPadding={true}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="w-[78vw] sm:w-[44vw] md:w-[28vw] lg:w-[220px]">
                <AspectRatio ratio={9/16}>
                  <Skeleton className="h-full w-full rounded-lg" />
                </AspectRatio>
              </div>
            ))}
          </HorizontalScroller>
        </div>
      ) : !showError ? (
        <div className="bleed-right-mobile">
          <HorizontalScroller spacing="custom-10px" disableScale={true} snap={true} snapAlign="start" snapStopAlways={true} showNextCard={true} noLeftPadding={true}>
            {displayShorts.map((item, index) => (
              <div key={item.id} className="w-[78vw] sm:w-[44vw] md:w-[28vw] lg:w-[220px]">
                {index === 0 ? (
                  <InlineShortPlayer
                    id={item.id}
                    title={item.title}
                    thumbnailUrl={item.thumbnailUrl}
                    onClick={() => setModalShort({ id: item.id, title: item.title })}
                  />
                ) : (
                  <ShortsCard 
                    id={item.id} 
                    title={item.title} 
                    thumbnailUrl={item.thumbnailUrl}
                    onClick={() => setModalShort({ id: item.id, title: item.title })}
                  />
                )}
              </div>
            ))}
          </HorizontalScroller>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Could not load Shorts right now. Visit our YouTube channel.</p>
      )}
      
      {modalShort && (
        <ShortsModal
          isOpen={true}
          onClose={() => setModalShort(null)}
          shortId={modalShort.id}
          title={modalShort.title}
        />
      )}
    </section>
  );
};

export default YouTubeShortsSection;
